import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  CircularProgress,
  makeStyles
} from '@material-ui/core'
import clsx from 'clsx'
import { BUTTON_VARIANT, BUTTON_SIZES } from '../../constants'

const getMediumSizeStyles = (noPadding) => ({
  padding: noPadding ? '0' : '6px 22px 6px 22px',
  fontSize: '1rem'
})

const getButtonSizeStyles = (size, noPadding, isLoading) => {
  switch (size) {
    case BUTTON_SIZES.extraSmall: {
      return {
        padding: noPadding
          ? '0'
          : `4px ${isLoading ? 'calc(12px + 1.25rem)' : '10px'} 2px 8px`,
        fontSize: '0.75rem'
      }
    }
    case BUTTON_SIZES.small: {
      return {
        padding: noPadding
          ? '0'
          : `4px ${isLoading ? 'calc(10px + 1.25rem)' : '10px'} 2px 10px`,
        fontSize: '0.875rem'
      }
    }
    case BUTTON_SIZES.large: {
      return {
        padding: noPadding ? '0' : '10px 22px 10px 22px',
        fontSize: '1.25rem',
        borderRadius: 'var(--border-radius-round)'
      }
    }
    default:
      return getMediumSizeStyles(noPadding)
  }
}

const CIRCULAR_PROGRESS_SIZES = {
  extraSmall: 15,
  small: 15,
  medium: 18,
  large: 20
}

const rightInset = {
  extraSmall: '-1rem',
  small: '-1rem',
  medium: 0,
  large: 0
}

const useStyles = makeStyles((theme) => ({
  base: ({ fullWidth, size, noPadding, isLoading, variant }) => ({
    textTransform: 'none',
    position: 'relative',
    fontWeight: 'bold',
    fontFamily: 'var(--root-font) !important',
    borderRadius: 'var(--border-radius-round)',
    padding: '4px 12px',
    boxShadow: 'none',
    ...getButtonSizeStyles(size, noPadding, isLoading),
    ...(fullWidth ? { width: '100%' } : {}),
    ...(variant === BUTTON_VARIANT.text
      ? {
        borderRadius: 'var(--border-radius-lg)',
        backgroundColor: 'var(--theme-color-surface-d0) !important'
      }
      : {}),
    ...(variant === BUTTON_VARIANT.ghost
      ? {
        backgroundColor: 'transparent !important',
        '&:hover': {
          backgroundColor: 'var(--theme-color-surface-d0) !important'
        }
      }
      : {})
  }),
  primary: {
    backgroundColor: 'var(--color-primary-500)',
    '&:hover': {
      background: 'var(--color-primary-600) !important',
      color: 'var(--color-contrast-primary-600)'
    },
    '&:disabled': {
      color: 'var(--color-neutral-050) !important',
      background: 'var(--color-neutral-500) !important'
    },
    color: 'var(--color-contrast-primary-600) !important'
  },
  outlined: {
    backgroundColor: 'var(--theme-color-surface-d0)',
    border: 'var(--border-primary-normal)',
    color: 'var(--color-primary-500)'
  },
  outlinedPrimary: {
    backgroundColor: 'var(--color-primary-500)',
    '&:hover': {
      backgroundColor: 'var(--color-primary-600) !important',
      color: 'var(--color-contrast-primary-600)'
    },
    color: 'var(--color-contrast-primary-600) !important'
  },
  secondary: {
    backgroundColor: 'var(--theme-color-surface-d0)',
    '&:hover': {
      borderColor: 'var(--border-color-primary-normal) !important',
      backgroundColor: 'var(--color-surface-100) !important',
      color: 'var(--color-primary-500) !important'
    },
    '&:disabled': {
      color: 'var(--color-neutral-300) !important',
      borderColor: 'var(--border-color-neutral-normal) !important',
      backgroundColor: 'var(--theme-color-surface-d0) !important'
    },
    color: 'var(--text-color-main) !important',
    border: 'var(--border-primary-normal)',
    padding: '4px 22px !important'
  },
  tertiary: {
    backgroundColor: 'var(--color-surface-100)',
    '&:hover': {
      backgroundColor: 'var(--color-surface-100) !important',
      color: 'var(--text-color-main) !important'
    },
    '&:disabled': {
      color: 'var(--color-neutral-300) !important',
      background: 'linear-gradient(0deg, rgba(238, 240, 248, 0.50) 0%, rgba(238, 240, 248, 0.50) 100%), #FFF !important'
    },
    color: 'var(--text-color-main) !important'
  },
  disabled: ({ variant }) => ({
    backgroundColor: 'var(--color-primary-200) !important',
    color: 'var(--color-primary-050) !important',
    ...(variant === BUTTON_VARIANT.text
      ? {
        opacity: 0.5,
        backgroundColor: 'var(--theme-color-surface-d0) !important',
        color: 'var(--text-color-main) !important'
      }
      : {})
  }),
  circularProgress: ({ size }) => ({
    color: 'var(--color-neutral-500)',
    position: 'absolute',
    display: 'flex',
    top: '50%',
    transform: 'translateY(-50%)',
    right: rightInset[size]
  }),
  label: {
    minWidth: '44px',
    position: 'relative'
  }
}))

const RoundedButton = ({
  id,
  size,
  type,
  noPadding,
  disabled,
  primary,
  secondary,
  tertiary,
  variant,
  onClick,
  children,
  className,
  fullWidth,
  isLoading,
  disabledClassName,
  startIcon
}) => {
  const classes = useStyles({ fullWidth, size, noPadding, isLoading, variant })

  const buttonClassName = useMemo(() => {
    const { contained, outlined } = BUTTON_VARIANT

    const usePrimaryClass = primary && variant === contained
    const useOutlinedPrimaryClass = primary && variant === outlined
    const useSecondaryClass = secondary && variant === contained
    const useTertiaryClass = tertiary && variant === contained
    const useOutlined = variant === outlined

    return clsx(
      {
        [classes.outlined]: useOutlined,
        [classes.primary]: usePrimaryClass,
        [classes.outlinedPrimary]: useOutlinedPrimaryClass,
        [classes.secondary]: useSecondaryClass,
        [classes.tertiary]: useTertiaryClass
      },
      classes.base,
      className
    )
  }, [
    classes.base,
    classes.primary,
    classes.outlined,
    classes.outlinedPrimary,
    classes.secondary,
    classes.tertiary,
    primary,
    secondary,
    tertiary,
    variant,
    className
  ])

  const buttonClasses = useMemo(() => {
    return {
      disabled: clsx(classes.disabled, {
        [disabledClassName]: !!disabledClassName
      }),
      label: classes.label
    }
  }, [classes.label, classes.disabled, disabledClassName])

  return (
    <Button
      id={id}
      {...(size !== BUTTON_SIZES.extraSmall ? { size } : {})}
      variant={variant !== BUTTON_VARIANT.ghost ? variant : undefined}
      onClick={onClick}
      disabled={disabled}
      classes={buttonClasses}
      className={buttonClassName}
      startIcon={startIcon}
      {...(type ? { type } : {})}
    >
      {children}
      {isLoading && (
        <div className={classes.circularProgress}>
          <CircularProgress size={CIRCULAR_PROGRESS_SIZES[size]} />
        </div>
      )}
    </Button>
  )
}

RoundedButton.propTypes = {
  id: PropTypes.string,
  noPadding: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  disabled: PropTypes.bool,
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  tertiary: PropTypes.bool,
  variant: PropTypes.oneOf(Object.values(BUTTON_VARIANT)),
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.any,
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  type: PropTypes.string,
  disabledClassName: PropTypes.string,
  startIcon: PropTypes.node
}
RoundedButton.defaultProps = {
  id: undefined,
  noPadding: false,
  size: BUTTON_SIZES.medium,
  disabled: false,
  primary: false,
  secondary: false,
  tertiary: false,
  fullWidth: false,
  variant: BUTTON_VARIANT.contained,
  onClick: undefined,
  children: undefined,
  className: undefined,
  isLoading: false,
  type: undefined,
  disabledClassName: undefined
}

export default RoundedButton
